// Tabs, newlines and other control characters are stripped during URL parsing,
// so a scheme broken across a newline reaches the check already laundered into
// something that passes, while browsers still honour it as an href.
const hasControlCharacters = (value: string): boolean => {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code < 32 || code === 127) return true;
  }
  return false;
};

/**
 * The announcement CTA is a Super Admin controlled href rendered into every
 * user's page, signed in or not. A javascript: URL there is stored XSS
 * delivered platform wide by the most privileged account in the product, so
 * the scheme is allowlisted rather than denylisted.
 *
 * Shared by the API, which refuses to store an unsafe link, and by the bar,
 * which refuses to render one. The second check is not redundant: rows predate
 * validation changes, and the renderer should not depend on when a row was
 * written to stay safe.
 */
export function isSafeCtaUrl(value: string): boolean {
  if (hasControlCharacters(value)) return false;

  if (value.startsWith('/')) return isInternalCtaUrl(value);

  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

// Any host would do; it only has to be one no real link can resolve to.
const INTERNAL_BASE = 'https://internal.invalid';

/**
 * Decided by resolving against a known base and comparing origins, not by
 * counting leading slashes.
 *
 * A prefix test is not enough to tell a path from another origin. '//evil.com' is
 * protocol relative, and '/\evil.com' reaches the same place because the URL
 * parser folds a backslash into a slash for special schemes — both look like
 * paths and both resolve to a third party host. Resolving settles every such form
 * at once, including whatever the next one turns out to be.
 *
 * Kept in step with isSafeCtaUrl deliberately: if these two disagreed, a URL
 * could pass validation as safe and then be handed to the router as internal,
 * which is exactly how an attacker controlled host gets rendered without
 * rel="noopener noreferrer".
 */
export function isInternalCtaUrl(value: string): boolean {
  if (!value.startsWith('/')) return false;

  try {
    return new URL(value, INTERNAL_BASE).origin === INTERNAL_BASE;
  } catch {
    return false;
  }
}
